import { ref } from 'vue';
import { deepEqual } from '@/utils/formatters.js';

// Structural clone that preserves Date instances (unlike JSON.parse(JSON.
// stringify(...)), which turns Dates into strings and would make an untouched
// date field look "dirty" against a snapshot taken before any edit).
// Firestore Timestamps are immutable, so they're kept by reference rather
// than cloned.
function snapshotClone(value) {
  if (value === null || typeof value !== 'object') return value;
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value.toDate === 'function' && typeof value.toMillis === 'function') return value; // Firestore Timestamp
  if (Array.isArray(value)) return value.map(snapshotClone);
  const clone = {};
  for (const key of Object.keys(value)) clone[key] = snapshotClone(value[key]);
  return clone;
}

// Comparison-based dirty tracking: call markClean(value) whenever the current
// value is known to match what's saved (on load, and after every successful
// save/autosave), then call isDirty(value) to check it against that baseline.
// Uses deepEqual rather than a manually-flipped boolean flag, so it can't
// drift out of sync with what actually changed — and deepEqual (unlike
// JSON.stringify) handles Dates/Timestamps and key order correctly.
export function useDirtyTracking() {
  const snapshot = ref(undefined);
  const hasSnapshot = ref(false);

  function markClean(value) {
    snapshot.value = snapshotClone(value);
    hasSnapshot.value = true;
  }

  function isDirty(currentValue) {
    if (!hasSnapshot.value) return false;
    return !deepEqual(currentValue, snapshot.value);
  }

  // Field-level diff against the baseline: only keys whose value actually
  // changed. Used to save just the fields a user edited, rather than the
  // whole object, so a field an external writer touched while the form was
  // open (e.g. create_folder.py's stage/rating write-back) isn't clobbered
  // by a stale value the form never displayed as changed.
  function getChangedFields(currentValue) {
    const current = currentValue || {};
    if (!hasSnapshot.value) return { ...current };
    const base = snapshot.value || {};
    const changed = {};
    for (const key of new Set([...Object.keys(current), ...Object.keys(base)])) {
      if (!deepEqual(current[key], base[key])) changed[key] = current[key];
    }
    return changed;
  }

  // Advances the baseline for just the given fields, leaving every other
  // field's baseline untouched. Used when an external write (e.g. a live
  // Firestore sync) is merged into the form for fields the user hasn't
  // touched: those fields should stop showing as unsaved changes, but a
  // real, still-unsaved user edit to some OTHER field must keep registering
  // as dirty. A blanket markClean() would wrongly clear that too.
  function updateBaselineFields(fields) {
    if (!hasSnapshot.value || !fields) return;
    const patch = {};
    for (const key of Object.keys(fields)) patch[key] = snapshotClone(fields[key]);
    snapshot.value = { ...snapshot.value, ...patch };
  }

  function reset() {
    snapshot.value = undefined;
    hasSnapshot.value = false;
  }

  return { markClean, isDirty, getChangedFields, updateBaselineFields, reset };
}
